import React, {useState} from 'react';
import {View, StyleSheet, Linking, TouchableOpacity} from 'react-native';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import Spacer from '@/Atoms/Spacer';
import CouponCode from '@/Atoms/CouponCode';
import CCColors from '@/Utils/CCColors';
import {FontSizes, vs} from '@/Utils/Dimensions';
import {computeFinalPrice, useCouponStore} from '@/Store/couponStore';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

interface EntryFeeSelectProps {
  errorMessage?: string;
  tournament?: any;
  categories: Record<string, number>;
  discountDetails?: Record<string, any>;
  handlePayment: (category: string, amount: number) => void;
  isPaymentProcessing: boolean;
  continuePaymentUrl?: string | null;
  pendingOrder?: any;
  setContinuePaymentUrl?: (url: string | null) => void;
  couponExtraParams?: Record<string, unknown>;
  isMembershipPurchase?: boolean;
}

export default function EntryFeeSelect({
  errorMessage,
  tournament,
  categories,
  discountDetails,
  handlePayment,
  isPaymentProcessing,
  continuePaymentUrl,
  pendingOrder,
  setContinuePaymentUrl,
  couponExtraParams,
  isMembershipPurchase,
}: EntryFeeSelectProps) {
  const [selectedCategory, setSelectedCategory] = useState<{
    key: string;
    label: string;
    value: number;
  } | null>(null);
  const appliedCoupon = useCouponStore(s => s.appliedCoupon);
  const pendingCoupon = useCouponStore(s => s.pendingCoupon);

  const systemDiscountedPrice: number | null = (() => {
    if (!selectedCategory || !discountDetails?.[selectedCategory.key]) {
      return null;
    }
    const disc = discountDetails[selectedCategory.key];
    const base = selectedCategory.value;
    if (typeof disc === 'number') {
      return Math.max(0, base - disc);
    }
    if (disc && typeof disc === 'object') {
      if (typeof disc.discounted_price === 'number') {
        return disc.discounted_price;
      }
      if (typeof disc.discount_amount === 'number') {
        return Math.max(0, base - disc.discount_amount);
      }
      if (typeof disc.price === 'number') {
        return disc.price;
      }
    }
    return null;
  })();

  const base = selectedCategory?.value ?? 0;
  const couponPrice = appliedCoupon
    ? computeFinalPrice(appliedCoupon, base)
    : null;
  const pendingDiscountedAmount = pendingCoupon
    ? computeFinalPrice(pendingCoupon.coupon, base)
    : null;

  const couponSavings =
    couponPrice !== null && base > 0 ? base - couponPrice : 0;

  const finalAmount =
    couponPrice !== null
      ? couponPrice
      : systemDiscountedPrice !== null
      ? systemDiscountedPrice
      : base;

  const categoryEntries = Object.entries(categories || {}).map(
    ([key, value]) => ({key, label: key, value: Number(value)}),
  );

  const isCtaDisabled = !selectedCategory || isPaymentProcessing;

  return (
    <View>
      {/* Membership purchase banner */}
      {isMembershipPurchase && (
        <View style={styles.membershipBanner}>
          <CCText
            color={CCColors.Primary.Aqua}
            fontSize={FontSizes[14]}
            fontWeight="semibold">
            Purchase Membership
          </CCText>
          <CCText
            color="#888280"
            fontSize={FontSizes[12]}
            style={styles.membershipBannerSubtext}>
            Select a membership plan to continue with tournament registration
          </CCText>
        </View>
      )}

      {/* Pending payment banner */}
      {continuePaymentUrl && pendingOrder && (
        <>
          <View
            style={styles.pendingBanner}
            data-testid="continue-payment-banner">
            <CCText
              color={CCColors.Orange}
              fontSize={FontSizes[14]}
              fontWeight="semibold">
              You have an existing incomplete payment
            </CCText>
            <CCText
              color="#888280"
              fontSize={FontSizes[12]}
              style={{marginTop: 4}}>
              Complete your previous payment to finish registration
            </CCText>
          </View>
          <CCButton
            type="outline"
            disabled={isPaymentProcessing}
            onPress={() => {
              if (continuePaymentUrl) {
                Linking.openURL(continuePaymentUrl);
                setContinuePaymentUrl?.(null);
              }
            }}
            style={{borderColor: CCColors.Orange, marginBottom: vs(12)}}
            data-testid="continue-payment-btn">
            <CCText color={CCColors.Orange} fontWeight="semibold">
              Continue Payment
            </CCText>
          </CCButton>
          <View style={styles.orDivider}>
            <View style={styles.orLine} />
            <CCText
              fontSize={12}
              color="#888280"
              style={{marginHorizontal: 10}}>
              or start new
            </CCText>
            <View style={styles.orLine} />
          </View>
        </>
      )}

      {errorMessage ? (
        <>
          <CCText color={CCColors.Primary.Aqua} fontSize={FontSizes[14]}>
            {errorMessage}
          </CCText>
          <Spacer spacing={vs(12)} />
        </>
      ) : null}

      {/* Radio options */}
      <Spacer spacing={vs(10)} />
      {categoryEntries.map((item, index) => {
        const isSelected = selectedCategory?.key === item.key;
        const eligText = discountDetails?.[item.key]?.eligibility;
        return (
          <TouchableOpacity
            key={item.key}
            style={[styles.efcOpt, isSelected && styles.efcOptSel]}
            onPress={() => setSelectedCategory(item)}
            activeOpacity={0.7}
            data-testid="category-radio">
            {/* Radio */}
            <View style={[styles.efcRadio, isSelected && styles.efcRadioSel]}>
              {isSelected && <View style={styles.efcRadioDot} />}
            </View>

            {/* Name + eligibility + featured flag */}
            <View style={styles.efcOptBody}>
              <View style={styles.efcOptNameRow}>
                <CCText fontSize={14} fontWeight="semibold" color="#262322">
                  {item.label}
                </CCText>
                {index === 0 && categoryEntries.length > 1 && (
                  <View style={styles.efcFlag}>
                    <CCText
                      fontSize={9}
                      fontWeight="bold"
                      color="#262322"
                      style={{letterSpacing: 0.54, textTransform: 'uppercase'}}>
                      MOST CHOSEN
                    </CCText>
                  </View>
                )}
              </View>
              {eligText ? (
                <CCText fontSize={11} color="#888280" style={{marginTop: 2}}>
                  {eligText}
                </CCText>
              ) : null}
            </View>

            {/* Price with Thunder font */}
            <View style={styles.efcOptPriceWrap}>
              <CCText fontSize={14} color="#262322" style={{marginRight: 2}}>
                ₹
              </CCText>
              <CCText fontSize={22} fontWeight="thunder" color="#262322">
                {item.value}
              </CCText>
            </View>
          </TouchableOpacity>
        );
      })}

      {/* Divider + Summary */}
      {selectedCategory && (
        <>
          <View style={styles.efcDivider} />

          <View style={styles.efcSummary}>
            <View style={styles.efcSummaryRow}>
              <CCText fontSize={13} color="#4D3F37">
                {selectedCategory.label} entry
              </CCText>
              <CCText fontSize={13} color="#4D3F37">
                ₹{selectedCategory.value}
              </CCText>
            </View>

            {appliedCoupon && couponSavings > 0 && (
              <View style={styles.efcSummaryRow}>
                <CCText fontSize={13} color="#4B968D">
                  Coupon {appliedCoupon.code}
                </CCText>
                <CCText fontSize={13} color="#4B968D">
                  −₹{couponSavings}
                </CCText>
              </View>
            )}

            <View style={styles.efcSummaryRule} />

            <View style={styles.efcSummaryRow}>
              <CCText fontSize={14} fontWeight="semibold" color="#262322">
                Total
              </CCText>
              <View style={styles.efcTotalAmount}>
                <CCText fontSize={14} color="#262322" style={{marginRight: 2}}>
                  ₹
                </CCText>
                <CCText fontSize={24} fontWeight="thunder" color="#262322">
                  {finalAmount}
                </CCText>
              </View>
            </View>
          </View>
        </>
      )}

      <Spacer spacing={vs(14)} />

      {/* Coupon */}
      <CouponCode
        basePrice={selectedCategory?.value ?? 0}
        currentEffectivePrice={
          appliedCoupon
            ? couponPrice ?? undefined
            : systemDiscountedPrice ?? undefined
        }
        discountedPrice={couponPrice}
        currentDiscountLabel={
          !appliedCoupon && systemDiscountedPrice !== null
            ? 'Current Offer'
            : undefined
        }
        pendingDiscountedPrice={pendingDiscountedAmount}
        couponExtraParams={couponExtraParams}
      />

      <Spacer spacing={vs(16)} />

      {/* CTA — custom TouchableOpacity, NOT CCButton */}
      <TouchableOpacity
        style={[styles.efcCta, isCtaDisabled && styles.efcCtaDisabled]}
        disabled={isCtaDisabled}
        activeOpacity={0.85}
        onPress={() => {
          if (!isPaymentProcessing && selectedCategory) {
            handlePayment(selectedCategory.key, finalAmount);
          }
        }}
        data-testid="proceed-to-payment-btn">
        <CCText
          fontSize={15}
          fontWeight="bold"
          color={isCtaDisabled ? 'rgba(77,63,55,0.42)' : '#262322'}
          style={{textAlign: 'center'}}>
          {isPaymentProcessing
            ? 'Processing...'
            : `Pay ₹${finalAmount} & Register`}
        </CCText>
      </TouchableOpacity>

      {/* Trust signals */}
      <View style={styles.efcTrust}>
        <MaterialCommunityIcons name="lock-outline" size={12} color="#888280" />
        <CCText fontSize={11} color="#888280">
          Secure UPI, Cards & Netbanking
        </CCText>
        <CCText fontSize={11} color="#888280" style={{opacity: 0.4}}>
          ·
        </CCText>
        <MaterialCommunityIcons
          name="shield-check-outline"
          size={12}
          color="#888280"
        />
        <CCText fontSize={11} color="#888280">
          7-day refund
        </CCText>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  /* ── Pending payment ── */
  pendingBanner: {
    backgroundColor: 'rgba(225, 120, 70, 0.12)',
    padding: 15,
    borderRadius: 8,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E17846',
  },
  orDivider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  orLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#E8E8E8',
  },

  /* ── Option rows ── */
  efcOpt: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    backgroundColor: '#FFFFFF',
    borderWidth: 1.5,
    borderColor: 'rgba(38, 35, 34, 0.10)',
    borderRadius: 12,
    marginBottom: 6,
  },
  efcOptSel: {
    borderColor: '#D1AB41',
    backgroundColor: '#FFFDF5',
  },

  /* ── Radio ── */
  efcRadio: {
    width: 20,
    height: 20,
    borderRadius: 999,
    borderWidth: 2,
    borderColor: 'rgba(77, 63, 55, 0.25)',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  efcRadioSel: {
    borderColor: '#D1AB41',
  },
  efcRadioDot: {
    width: 9,
    height: 9,
    borderRadius: 999,
    backgroundColor: '#D1AB41',
  },

  /* ── Option body (name + elig) ── */
  efcOptBody: {
    flex: 1,
  },
  efcOptNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },

  /* ── Option price ── */
  efcOptPriceWrap: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },

  /* ── Featured flag ── */
  efcFlag: {
    backgroundColor: '#FACF47',
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 999,
  },

  /* ── Divider ── */
  efcDivider: {
    height: 1,
    backgroundColor: 'rgba(38, 35, 34, 0.10)',
    marginTop: 18,
    marginBottom: 14,
  },

  /* ── Summary ── */
  efcSummary: {
    gap: 6,
  },
  efcSummaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  efcSummaryRule: {
    height: 1,
    backgroundColor: 'rgba(38, 35, 34, 0.10)',
    marginVertical: 6,
  },
  efcTotalAmount: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },

  /* ── CTA button ── */
  efcCta: {
    width: '100%',
    paddingVertical: 15,
    borderRadius: 32,
    backgroundColor: '#FACF47',
    alignItems: 'center',
    justifyContent: 'center',
    // 3D raised effect
    shadowColor: '#D1AB41',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 4,
  },
  efcCtaDisabled: {
    backgroundColor: '#FEF2B5',
    shadowColor: '#E8D98A',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.6,
    shadowRadius: 0,
    elevation: 2,
  },

  /* ── Membership banner ── */
  membershipBanner: {
    backgroundColor: 'rgba(75, 150, 141, 0.08)',
    padding: 16,
    borderRadius: 14,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: 'rgba(75, 150, 141, 0.2)',
  },
  membershipBannerSubtext: {
    marginTop: 4,
  },

  /* ── Trust row ── */
  efcTrust: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginTop: 12,
  },
});
